<template>
    <div class="container py-8">
        <!-- Loading -->
        <div v-if="pending" class="flex justify-center py-20">
            <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>

        <!-- Restricted (TOP-only mentor) -->
        <div v-else-if="isRestricted" class="text-center py-20 max-w-lg mx-auto">
            <div class="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-[rgba(4,114,116,0.1)]">
                <CIcon name="mentoring" class="text-[#047274] dark:text-primary-400" :size="32" />
            </div>
            <h1 class="text-2xl font-bold text-gray-900 mb-3 dark:text-txt-headings">Tento profil je dostupný len pre účastníkov Mentoringového networku</h1>
            <p class="text-gray-600 mb-6 dark:text-txt-body">Mentor je súčasťou Mentoringového networku. Pre zobrazenie profilu sa prihlás alebo sa zapoj do programu.</p>
            <div class="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <NuxtLink
                    to="/mentoring-network-pre-svetove-slovensko#prihlaska"
                    class="inline-flex items-center justify-center rounded-lg bg-[#047274] px-6 py-3 font-semibold text-white transition-colors hover:bg-[#035f61]"
                >
                    Prihlásiť sa do Mentoringového networku
                </NuxtLink>
                <NuxtLink to="/mentori" class="text-primary-500 hover:text-primary-600 dark:hover:text-primary-300">← Späť na mentorov</NuxtLink>
            </div>
        </div>

        <!-- Private mentor profile (SSR shows this; owners get re-fetched + revealed in onMounted) -->
        <div v-else-if="isVisibilityBlocked" class="text-center py-20 max-w-lg mx-auto">
            <div class="mb-6 inline-flex items-center justify-center w-16 h-16 rounded-full bg-[rgba(4,114,116,0.1)]">
                <CIcon name="mentoring" class="text-[#047274] dark:text-primary-400" :size="32" />
            </div>
            <h1 class="text-2xl font-bold text-gray-900 mb-3 dark:text-txt-headings">Profil nie je verejne dostupný</h1>
            <p class="text-gray-600 mb-6 dark:text-txt-body">Mentor svoj profil aktuálne nezverejnil. Skús to neskôr, alebo sa pozri na ostatných mentorov.</p>
            <NuxtLink to="/mentori" class="text-primary-500 hover:text-primary-600 dark:hover:text-primary-300">← Späť na mentorov</NuxtLink>
        </div>

        <!-- Not found -->
        <div v-else-if="!profile" class="text-center py-20">
            <h1 class="text-2xl font-bold text-gray-900 mb-4 dark:text-txt-headings">Mentor nenájdený</h1>
            <NuxtLink to="/mentori" class="text-primary-500 hover:text-primary-600 dark:hover:text-primary-300">← Späť na mentorov</NuxtLink>
        </div>

        <!-- Profile -->
        <div v-else class="max-w-5xl mx-auto">
            <!-- Back link -->
            <a
                href="#"
                class="inline-flex items-center text-gray-600 hover:text-primary-500 mb-6 dark:text-txt-body"
                @click.prevent="goBack"
            >
                ← Späť na mentorov
            </a>

            <!-- Header section -->
            <div class="flex flex-col md:flex-row gap-8 mb-8">
                <!-- Avatar -->
                <div class="shrink-0">
                    <img
                        v-if="profile.profileImg"
                        :src="resizedImageSrc(profile.profileImg, 208, 208)"
                        :srcset="resizedImageSrcset(profile.profileImg, 208, 208)"
                        :alt="mentorFullName"
                        width="208"
                        height="208"
                        fetchpriority="high"
                        class="w-44 h-44 md:w-52 md:h-52 rounded-lg object-cover"
                    />
                    <div
                        v-else
                        class="w-44 h-44 md:w-52 md:h-52 rounded-lg bg-primary-100 flex items-center justify-center dark:bg-primary-900/30"
                    >
                        <span class="text-5xl font-bold text-primary-500">{{ initials }}</span>
                    </div>
                </div>

                <!-- Info -->
                <div class="flex-1">
                    <div class="flex items-center gap-3 mb-3 flex-wrap">
                        <h1 class="text-3xl md:text-4xl font-bold text-gray-900 dark:text-txt-headings">{{ mentorFullName }}</h1>
                        <CBadge v-if="isTopMentor" type="teal">TOP MENTOR</CBadge>
                        <CBadge v-if="isBooked" type="orange" title="Mentor má aktívny mentoring">Obsadené kapacity</CBadge>
                    </div>

                    <!-- Current job -->
                    <p v-if="currentJob" class="text-sm text-gray-600 mb-2 flex items-center dark:text-txt-body">
                        <CIcon name="jobs" class="size-5 mr-2 text-gray-400" /> {{ currentJob }}
                    </p>

                    <!-- Education -->
                    <p v-if="latestEducation" class="text-sm text-gray-600 mb-2 flex items-center dark:text-txt-body">
                        <CIcon name="skills" class="size-5 mr-2 text-gray-400" /> {{ latestEducation }}
                    </p>

                    <!-- Location -->
                    <p v-if="location" class="text-sm text-gray-600 mb-4 flex items-center dark:text-txt-body">
                        <CIcon name="location-outline" class="size-5 mr-2 text-gray-400" /> {{ location }}
                    </p>

                </div>
            </div>

            <!-- CTA Button + Actions row -->
            <div class="flex flex-wrap items-center gap-4 mb-8 pb-8 border-b border-gray-200 dark:border-default">
                <AskForMentorButton :mentor="profile" :profile-owner="isMineMentoringProfile" />

                <div class="flex items-center ml-auto">
                    <CFavouriteButton :id="profile.id" type="mentor" :title="mentorFullName" />

                    <CShareButton :id="profile.id" type="mentor" :title="mentorFullName" />

                    <CReportButton :id="profile.id" type="mentor" :title="mentorFullName" />
                </div>
            </div>

            <!-- Mentoring Highlight Card -->
            <div
                v-if="mentorMotivation || interests.length"
                class="bg-[rgba(4,114,116,0.04)] border border-[rgba(4,114,116,0.15)] rounded-xl p-6 mb-8"
            >
                <!-- Motivation -->
                <div v-if="mentorMotivation" class="mb-6">
                    <h3 class="text-lg font-bold text-gray-900 mb-2 dark:text-txt-headings">Prečo chcem byť mentorom/kou</h3>
                    <p class="text-gray-700 whitespace-pre-line dark:text-txt-body">{{ mentorMotivation }}</p>
                </div>

                <!-- Mentoring interests -->
                <div v-if="interests.length">
                    <h3 class="text-sm font-semibold text-gray-900 mb-2 dark:text-txt-headings">S čím viem pomôcť</h3>
                    <div class="flex flex-wrap gap-2">
                        <NuxtLink
                            v-for="interest in interests"
                            :key="interest"
                            :to="{ name: 'mentori', query: { interests: interest } }"
                            class="px-3 py-1 bg-white/80 text-[#047274] border border-[rgba(4,114,116,0.2)] rounded-md text-sm hover:bg-white transition-colors dark:hover:bg-surface-card dark:bg-surface-card/80 dark:text-primary-400"
                        >
                            # {{ interest }}
                        </NuxtLink>
                    </div>
                </div>
            </div>

            <!-- Single column content -->
            <div class="space-y-8">
                <!-- Viac o mne -->
                <section v-if="profile.hobby || profile.shortProfile">
                    <h2 class="text-xl font-bold text-gray-900 mb-4 dark:text-txt-headings">Viac o mne</h2>
                    <div class="bg-gray-50 rounded-lg p-5 dark:bg-surface-subtle">
                        <div
                            class="prose prose-gray max-w-none text-gray-700 dark:text-txt-body"
                            v-html="profile.hobby || profile.shortProfile"
                        ></div>
                    </div>
                </section>

                <!-- Kľúčové slová o mne (general interests, deduped from mentoring interests) -->
                <section v-if="generalInterests.length">
                    <h2 class="text-xl font-bold text-gray-900 mb-4 dark:text-txt-headings">Kľúčové slová o mne</h2>
                    <div class="flex flex-wrap gap-2">
                        <span
                            v-for="keyword in generalInterests"
                            :key="keyword"
                            class="px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm dark:bg-surface-subtle dark:text-txt-body"
                        >
                            # {{ keyword }}
                        </span>
                    </div>
                </section>

                <!-- Pracovné skúsenosti -->
                <section v-if="mentorJobs.length">
                    <h2 class="text-xl font-bold text-gray-900 mb-4 dark:text-txt-headings">Pracovné skúsenosti</h2>
                    <div class="space-y-6">
                        <div v-for="(job, index) in mentorJobs" :key="index" class="flex gap-4">
                            <div class="shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center dark:bg-surface-subtle">
                                <CIcon name="jobs" class="size-5 text-gray-500 dark:text-txt-body" />
                            </div>
                            <div class="flex-1">
                                <div class="flex justify-between items-start">
                                    <div>
                                        <h3 class="font-semibold text-gray-900 dark:text-txt-headings">
                                            {{ job.position }} @ {{ job.org }}
                                        </h3>
                                        <p v-if="job.location" class="text-sm text-gray-500 dark:text-txt-body">{{ job.location }}</p>
                                    </div>
                                    <span v-if="job.startYear" class="text-sm text-gray-500 whitespace-nowrap dark:text-txt-body">
                                        {{ job.startYear }} - {{ job.endYear || 'súčasnosť' }}
                                    </span>
                                </div>
                                <p v-if="job.description" class="mt-2 text-gray-600 text-sm whitespace-pre-line dark:text-txt-body">
                                    {{ job.description }}
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Vzdelanie -->
                <section v-if="profile.universities?.length">
                    <h2 class="text-xl font-bold text-gray-900 mb-4 dark:text-txt-headings">Vzdelanie</h2>
                    <div class="space-y-6">
                        <div v-for="(uni, index) in profile.universities" :key="index" class="flex gap-4">
                            <div class="shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center dark:bg-surface-subtle">
                                <CIcon name="skills" class="size-5 text-gray-500 dark:text-txt-body" />
                            </div>
                            <div class="flex-1">
                                <div class="flex justify-between items-start">
                                    <div>
                                        <h3 class="font-semibold text-gray-900 dark:text-txt-headings">{{ uni.school }}</h3>
                                        <p v-if="uni.field" class="text-sm text-gray-500 dark:text-txt-body">{{ uni.field }}</p>
                                    </div>
                                    <span v-if="uni.startYear" class="text-sm text-gray-500 whitespace-nowrap dark:text-txt-body">
                                        {{ uni.startYear }} - {{ uni.endYear || 'súčasnosť' }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <!-- Location map -->
                <section v-if="isSlovakCity">
                    <h2 class="text-xl font-bold text-gray-900 mb-4 dark:text-txt-headings">Lokalita</h2>
                    <div class="bg-white border border-gray-200 rounded-lg overflow-hidden dark:bg-surface-card dark:border-default">
                        <CSlovakiaMap
                            :locations="mapLocations"
                            :user-city="currentUserCity"
                            :show-distance="!!currentUserCity"
                            variant="light"
                        />
                        <p class="text-center text-sm text-gray-600 py-3 dark:text-txt-body">{{ location }}</p>
                    </div>
                </section>
            </div>

            <!-- Recommended mentors -->
            <RecommendedItems v-if="isMentorPublished" :id="profile.id" collection="mentors" class="mt-12">
                {{ recommendedListHeading }}
            </RecommendedItems>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { JobPosition, UserProfile } from '#shared/types/User'
import { OverviewType } from '#shared/types/Overview'
import { VisibilityType } from '#shared/types/visibility'
import { getFullName } from '#shared/utils'
import { getProfileJobDescription, getProfileJobLocation } from '#shared/utils/profileJobs'
import AskForMentorButton from '@/components/mentor/AskForMentorButton.vue'
import CBadge from '@/components/ui/CBadge.vue'
import CFavouriteButton from '@/components/ui/CFavouriteButton.vue'
import CIcon from '@/components/ui/CIcon.vue'
import CReportButton from '@/components/ui/CReportButton.vue'
import CShareButton from '@/components/ui/CShareButton.vue'
import CSlovakiaMap from '@/components/ui/CSlovakiaMap.vue'
import { useExponea } from '@/composables/exponea/useExponea'
import { useJsonLd } from '@/composables/head/useJsonLd'
import { resizedImageSrc, resizedImageSrcset } from '@/utils/resizedImage'
import { useSharing } from '@/composables/sharing/useSharing'
import { useTopMentoringAccess } from '@/composables/mentoring/useTopMentoringAccess'
import {
    absoluteUrl,
    buildBreadcrumbList,
    buildProfilePage,
    collapseWhitespace,
    composeDescription,
    getOngoingJob,
    htmlToPlainText,
    SECTION_SOCIAL_IMAGES,
    withSiteName
} from '@/utils/structuredData'

const { getSharingUrl } = useSharing()
const { trackExponeaEvent } = useExponea()

const route = useRoute()
const router = useRouter()
const store = useApplicationStore()
const { isTopParticipant } = useTopMentoringAccess()
const { t: $t } = useI18n()

// Back navigation: go back and scroll to the mentor card
function goBack() {
    if (import.meta.client) {
        sessionStorage.setItem('scrollToCard', `r-id-${mentorId}`)
        if (window.history.length > 1) {
            router.back()
            return
        }
    }
    navigateTo('/mentori')
}

// Data from route params
const mentorId = route.params.id as string

// Get fallback ID from query params (passed by Typesense search results)
const fallbackId = route.query.uid as string | undefined

// Build auth headers for client-side requests (needed for TOP-approved mentor access check)
const authHeaders = ref<Record<string, string>>({})
if (import.meta.client) {
    try {
        const nuxtApp = useNuxtApp()
        const auth = nuxtApp.$auth as any
        if (auth?.currentUser) {
            const token = await auth.currentUser.getIdToken()
            if (token) {
                authHeaders.value = { authorization: `Bearer ${token}` }
            }
        }
    } catch {}
}

// Fetch data using server API (works on both SSR and client).
// Forward ?uid= as a disambiguator so the API picks the right doc when slugs collide.
// `role=mentor` lets the server enforce mentorProfile.visibility for non-owners.
const { data, error, status, refresh } = await useFetch<UserProfile>(`/api/profiles/${mentorId}`, {
    key: `mentor-profile-${mentorId}-${fallbackId || ''}`,
    query: fallbackId ? { uid: fallbackId, role: 'mentor' } : { role: 'mentor' },
    headers: authHeaders.value
})

// If primary failed and we have a fallback, try that
const { data: fallbackData } =
    fallbackId && !data.value
        ? await useFetch<UserProfile>(`/api/profiles/${fallbackId}`, {
              key: `mentor-profile-fallback-${fallbackId}`,
              query: { role: 'mentor' }
          })
        : { data: ref(null) }

// A slug that matches no profile must answer 404, not 200. useFetch captures the
// API's 404 into `error` instead of propagating it, so /mentor/<anything> used to
// render the page shell with HTTP 200 — an unbounded set of soft-404 URLs.
// Scoped to 404 on purpose: the 403 branches below ("restricted" for TOP mentors
// and "visibility-private") render their own friendly views and must not be touched.
if (error.value?.statusCode === 404 && !data.value && !fallbackData.value) {
    throw createError({ statusCode: 404, statusMessage: 'Mentor sa nenašiel', fatal: true })
}

// SSR has no auth → TOP mentor profiles return 403 during SSR.
// Re-fetch with auth credentials AFTER hydration (in onMounted) to avoid
// hydration mismatch — SSR renders "restricted" view with text-center,
// and changing data.value during setup would cause class bindings to stick.

// Track client-side auth re-fetch in progress (set in onMounted, avoids hydration mismatch)
const isLoadingAuth = ref(false)

// Use primary data or fallback data
const profile = computed(() => data.value || fallbackData.value || null)

// Defense-in-depth: if the server somehow returned a private profile doc
// (e.g. caller didn't pass ?role=mentor), still hide it from non-owners
// once we're on the client and can tell who the viewer is. Network mentors are
// exempt for network viewers: their own visibility is managed by GROWNi and the
// server already decided they are ready to be seen.
if (import.meta.client && profile.value?.mentorProfile) {
    const v = profile.value.mentorProfile.visibility
    const isHidden = v === VisibilityType.hidden || v === VisibilityType.draft
    const isNetworkMentorForNetworkViewer = profile.value.mentorTopApproved === true && isTopParticipant.value
    if (isHidden && store.userId !== profile.value.id && !isNetworkMentorForNetworkViewer) {
        await navigateTo({ path: '/ponuka-nie-je-aktualne-dostupna',
            query: { type: OverviewType.MENTORING, id: mentorId }
        })
    }
}

// Determine if profile is restricted (TOP-only) — show friendly message instead of error
const isRestricted = computed(() => {
    // While re-fetching with auth, don't show restricted message
    if (isLoadingAuth.value) return false
    // Server API returned 403 with statusMessage 'restricted' = TOP-approved mentor, user is not a TOP participant.
    if (
        error.value?.statusCode === 403 &&
        (error.value as any)?.statusMessage === 'restricted' &&
        !fallbackData.value
    )
        return true
    return false
})

// Determine if profile is private (server-enforced visibility gate). Shown inline
// during SSR (so bots/owner both see a placeholder). On client, onMounted re-fetches
// with auth: if owner, error clears and the profile renders; if non-owner, this
// stays true and onMounted then redirects to the friendlier "not available" page.
const isVisibilityBlocked = computed(() => {
    if (isLoadingAuth.value) return false
    return (
        error.value?.statusCode === 403 &&
        (error.value as any)?.statusMessage === 'visibility-private' &&
        !fallbackData.value
    )
})

const pending = computed(() => status.value === 'pending' || isLoadingAuth.value)

// Get current logged-in user's city for distance display
const currentUserCity = computed(() => store.userProfile?.geoCity || null)

type MentorJob = JobPosition & {
    location: string
    description: string
}

function isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.length > 0
}

// Computed properties
const mentorFullName = computed(() => {
    return profile.value ? getFullName(profile.value) : ''
})

const initials = computed(() => {
    if (!profile.value) return ''
    return `${profile.value.firstName?.[0] || ''}${profile.value.lastName?.[0] || ''}`.toUpperCase()
})

const currentJob = computed(() => {
    if (!mentorJobs.value.length) return ''
    const sorted = [...mentorJobs.value].sort((a, b) => (b.endYear || 9999) - (a.endYear || 9999))
    const latest = sorted[0]
    if (!latest) return ''
    return [latest.position, latest.org].filter(isNonEmptyString).join(' @ ')
})

const latestEducation = computed(() => {
    const unis = profile.value?.universities
    if (!unis?.length) return ''
    const sorted = [...unis].sort((a, b) => (b.endYear || 9999) - (a.endYear || 9999))
    const latest = sorted[0]
    if (!latest) return ''
    const endYear = typeof latest.endYear === 'number' ? `${latest.endYear}` : ''
    return [latest.school, endYear].filter(isNonEmptyString).join(', ')
})

const mentorJobs = computed<MentorJob[]>(() => {
    const jobs = profile.value?.jobs
    if (!jobs?.length) return []

    return jobs.map((job) => ({
        ...job,
        location: getProfileJobLocation(job),
        description: getProfileJobDescription(job)
    }))
})

const location = computed(() => {
    if (!profile.value) return ''
    const parts = [profile.value.geoCity, profile.value.geoCountry].filter(isNonEmptyString)
    return parts.join(', ') || profile.value.geoCountry || ''
})

const mapLocations = computed(() => {
    const city = profile.value?.geoCity
    return city ? [{ city, count: 1 }] : []
})

// Check if mentor is in Slovakia (show map only for Slovak cities)
const isSlovakCity = computed(() => {
    if (!profile.value?.geoCity) return false
    const country = profile.value.geoCountry?.toLowerCase() || ''
    return country.includes('slovak') || country.includes('slovensko') || country === 'sk'
})

const interests = computed(() => {
    const mentoring = profile.value?.mentorProfile?.interests
    if (mentoring?.length) return mentoring
    return profile.value?.mentorTopProfile?.interests || []
})

// TOP mentor badge
const isTopMentor = computed(() => profile.value?.mentorTopApproved === true)

// "Obsadené kapacity" badge (has active mentoring)
const isBooked = computed(() => profile.value?.mentorBooked === true)

// Motivation with fallback to TOP application q1
const mentorMotivation = computed(() => {
    return profile.value?.mentorProfile?.motivation || profile.value?.mentorTopProfile?.q1 || ''
})

// General interests minus mentoring interests (no duplication)
const generalInterests = computed(() => {
    const mentoringSet = new Set(interests.value)
    return (profile.value?.interests || []).filter((k: string) => !mentoringSet.has(k))
})

// Bio in the order the page shows it ("Viac o mne", then the motivation).
const mentorBio = computed(() => {
    return htmlToPlainText(profile.value?.hobby || profile.value?.shortProfile || mentorMotivation.value)
})

const mentorDescription = computed(() => {
    if (mentorBio.value) return composeDescription([mentorBio.value])
    return composeDescription([
        `${mentorFullName.value} – mentor/ka na GROWNi${currentJob.value ? ` (${currentJob.value})` : ''}.`,
        interests.value.length ? `S čím viem pomôcť: ${interests.value.slice(0, 5).join(', ')}.` : ''
    ])
})

const mentorImage = computed(() => {
    return profile.value?.profileImg ? getSharingUrl('profiles', profile.value.profileImg) : SECTION_SOCIAL_IMAGES.mentoring
})

const isMentorPublished = computed(() => {
    return profile.value?.mentorProfile?.visibility === VisibilityType.published
})

// The full profile is public only when the server returned it without auth (SSR) and the
// mentor profile is listed or shared by link. TOP-only and private profiles get no markup.
const isPublicMentorProfile = computed(() => {
    const visibility = profile.value?.mentorProfile?.visibility
    return (
        !!profile.value &&
        !isRestricted.value &&
        !isVisibilityBlocked.value &&
        (visibility === VisibilityType.published || visibility === VisibilityType.unlisted)
    )
})

// Unlisted mentor profiles are shared by link only: they render, but stay out of search and markup.
const isIndexableMentorProfile = computed(() => {
    return isPublicMentorProfile.value && profile.value?.mentorProfile?.visibility === VisibilityType.published
})

const canonicalUrl = computed(() => absoluteUrl(`/mentor/${profile.value?.slug || mentorId}`))

const mentorPageTitle = computed(() => {
    if (isRestricted.value) return withSiteName('Profil mentora z Mentoringového networku')
    if (isVisibilityBlocked.value) return withSiteName('Profil mentora nie je verejný')
    return withSiteName(mentorFullName.value ? `${mentorFullName.value} – mentor/ka` : 'Mentor')
})

const profilePageJsonLd = computed(() => {
    if (!profile.value || !isIndexableMentorProfile.value) return null
    const ongoingJob = getOngoingJob(mentorJobs.value)
    return buildProfilePage({
        name: mentorFullName.value,
        url: canonicalUrl.value,
        image: profile.value.profileImg,
        description: mentorBio.value ? composeDescription([mentorBio.value], 250) : undefined,
        jobTitle: ongoingJob?.position,
        worksFor: ongoingJob?.org,
        knowsAbout: interests.value
    })
})

useJsonLd(() => [
    profilePageJsonLd.value,
    isIndexableMentorProfile.value &&
        buildBreadcrumbList([
            { name: 'Domov', path: '/' },
            { name: 'Mentori', path: '/mentori' },
            { name: mentorFullName.value, path: canonicalUrl.value }
        ])
])

const isMineMentoringProfile = computed(() => {
    if (!import.meta.client) return false
    return store.userId === profile.value?.id
})

const recommendedListHeading = computed(() => {
    return $t('relatedItems.mentor.headline', 'sk') as string
})

// Head meta
useHead(() => ({
    title: mentorPageTitle.value,
    link: [
        // `key` dedupes against the site-wide canonical set in app.vue
        { rel: 'canonical', key: 'canonical', href: canonicalUrl.value }
    ],
    meta: isPublicMentorProfile.value
        ? [
              ...(isIndexableMentorProfile.value ? [] : [{ name: 'robots', content: 'noindex, follow' }]),
              { name: 'description', content: mentorDescription.value },
              { property: 'og:title', content: mentorPageTitle.value },
              { property: 'og:description', content: mentorDescription.value },
              { property: 'og:image', content: mentorImage.value },
              { property: 'og:image:alt', content: `Profilová fotka: ${mentorFullName.value}` },
              { property: 'og:type', content: 'profile' },
              { property: 'og:url', content: canonicalUrl.value }
          ]
        : // TOP-only and private profiles show the same placeholder under every URL.
          [{ name: 'robots', content: 'noindex, follow' }],
    script: [
        {
            key: 'gtm-mentor-page',
            innerHTML: `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'unique_id': '${profile.value?.id || ''}'
        });
      `,
            type: 'text/javascript'
        }
    ]
}))

// Re-fetch TOP mentor profile with auth after hydration, then set breadcrumb & track view
onMounted(async () => {
    // Re-fetch if SSR returned 403 and we have auth credentials
    if (error.value?.statusCode === 403 && authHeaders.value.authorization) {
        isLoadingAuth.value = true
        try {
            const result = await $fetch<UserProfile>(`/api/profiles/${mentorId}`, {
                query: fallbackId ? { uid: fallbackId, role: 'mentor' } : { role: 'mentor' },
                headers: { authorization: authHeaders.value.authorization }
            })
            data.value = result
            error.value = undefined
        } catch (e: any) {
            // If the re-fetch reveals the profile is private (vs TOP-restricted), redirect.
            if (e?.statusCode === 403 && e?.statusMessage === 'visibility-private') {
                await navigateTo({ path: '/ponuka-nie-je-aktualne-dostupna',
                    query: { type: OverviewType.MENTORING, id: mentorId }
                })
                return
            }
            // Still 403 → user genuinely doesn't have access, keep the SSR error
        } finally {
            isLoadingAuth.value = false
        }
    }

    if (process.client && mentorFullName.value) {
        // Use nextTick to ensure breadcrumbs component has provided the function
        nextTick(() => {
            const nuxtApp = useNuxtApp()
            const maybeDynamicTitle = nuxtApp.$dynamicTitle

            if (typeof maybeDynamicTitle === 'function') {
                maybeDynamicTitle(mentorFullName.value)
            } else {
                const maybeEmit = nuxtApp.emit
                if (typeof maybeEmit === 'function') {
                    maybeEmit('dynamicTitle', mentorFullName.value)
                }
            }
        })

        // Track mentor profile view
        if (profile.value && !isMineMentoringProfile.value) {
            trackExponeaEvent({
                eventName: 'mentoring_profile_view',
                userId: store.userId,
                eventProperties: {
                    mentor_id: profile.value.id,
                    mentor_slug: profile.value.slug,
                    mentor_name: mentorFullName.value
                }
            })
        }
    }
})
</script>
